/// <reference types="cloudflare-turnstile" />

import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-turnstile',
  template: `
    <ng-container *ngIf="isExecuting">
      <p class="text-align-center" aria-live="polite">
        Making sure you are not a bot <span class="blink">...</span>
      </p>
    </ng-container>
    <div #turnstileContainer class="turnstile-container"></div>
  `,
  styles: [':host { display: block; }'],
})
export class TurnstileComponent implements OnDestroy {
  @ViewChild('turnstileContainer', { static: true }) container!: ElementRef;
  @Output() verify = new EventEmitter<string>();
  @Output() error = new EventEmitter<any>();
  @Output() expired = new EventEmitter<void>();

  private widgetId: string | null = null;
  private scriptLoadPromise: Promise<void> | null = null;

  // Replaced Signal with a standard property
  public isExecuting = false;

  // Replaced inject() with constructor injection
  constructor() {}

  private get turnstile(): Turnstile.Turnstile | undefined {
    return typeof turnstile === 'undefined' ? undefined : turnstile;
  }

  private async executeWidget(): Promise<void> {
    if (!this.widgetId || !this.turnstile) {
      throw new Error('Turnstile widget not initialized');
    }

    this.turnstile.reset(this.widgetId);
    this.turnstile.execute(this.widgetId);
  }

  ngOnDestroy() {
    if (this.widgetId && this.turnstile) {
      try {
        this.turnstile.reset(this.widgetId);
        this.turnstile.remove(this.widgetId);
      } catch (error) {
        console.error('Error cleaning up Turnstile widget:', error);
      }
    }
  }

  private async ensureTurnstileInitialized(siteKey: string): Promise<void> {
    if (!this.turnstile) {
      await this.loadTurnstileScript();
    }

    if (!this.turnstile) {
      throw new Error('Turnstile API not initialized');
    }

    if (this.widgetId) {
      return;
    }

    const widgetId = this.turnstile.render(this.container.nativeElement, {
      sitekey: siteKey,
      callback: (token: string) => {
        this.isExecuting = false;
        this.verify.emit(token);
      },
      'error-callback': (error: any) => {
        this.isExecuting = false;
        this.error.emit(error);
      },
      'expired-callback': () => {
        this.isExecuting = false;
        this.expired.emit();
      },
      appearance: 'interaction-only',
      execution: 'execute',
    });

    if (!widgetId) {
      throw new Error('Turnstile widget failed to render');
    }

    this.widgetId = widgetId;
  }

  private loadTurnstileScript(): Promise<void> {
    if (this.turnstile) {
      return Promise.resolve();
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const scriptSrc =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      const existingScript = window.document.querySelector<HTMLScriptElement>(
        `script[src="${scriptSrc}"]`
      );

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), {
          once: true,
        });
        existingScript.addEventListener(
          'error',
          () => reject(new Error('Failed to load Turnstile script')),
          { once: true }
        );
        return;
      }

      const script = window.document.createElement('script');
      script.type = 'text/javascript';
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error('Failed to load Turnstile script'));
      window.document.head.appendChild(script);
    });

    return this.scriptLoadPromise.catch((error) => {
      this.scriptLoadPromise = null;
      throw error;
    });
  }

  public reset() {
    if (this.widgetId && this.turnstile) {
      this.turnstile.reset(this.widgetId);
    }

    this.isExecuting = false;
  }

  public execute(): Promise<void> {
    const siteKey = '0x4AAAAAAEND1XB_-WBSPeLf'; // Replace with your actual site key
    if (!siteKey) {
      this.verify.emit('LOCAL_TEST_TOKEN');
      return Promise.resolve();
    }

    if (this.isExecuting) {
      return Promise.resolve();
    }

    this.isExecuting = true;

    return new Promise<void>((resolve, reject) => {
      this.ensureTurnstileInitialized(siteKey)
        .then(async () => {
          if (!this.widgetId || !this.turnstile) {
            this.isExecuting = false;
            reject(new Error('Turnstile widget not initialized'));
            return;
          }

          await this.executeWidget();
          resolve();
        })
        .catch((error) => {
          this.isExecuting = false;
          this.error.emit(error);
          reject(error);
        });
    });
  }
}
